import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { deleteAccountStart } from "store/slices/authSlice";
import { enqueueSnackbar } from "notistack";
import theme from "theme";
import { useAuth } from "hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "utils/constants";

export default function AlertDialog({ open, handleClose }) {
  const [reason, setReason] = useState("");
  const reasonTooShort = reason.trim().length < 20;
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const resetForm = () => {
    handleClose();
    setReason("")
  };

  const handleDelete = () => {
    dispatch(
      deleteAccountStart({
        params: { reason },
        callback: (error, response) => {
          if (error) {
            enqueueSnackbar(`Error deleting account: ${error}`, {
              variant: "warning",
              anchorOrigin: {
                vertical: isMobile ? "bottom" : "top",
                horizontal: isMobile ? "center" : "right",
              },
            });
          } else {
            enqueueSnackbar("Account deleted successfully", {
              variant: "success",
              anchorOrigin: {
                vertical: isMobile ? "bottom" : "top",
                horizontal: isMobile ? "center" : "right",
              },
            });
            resetForm();
            logout();
            navigate(ROUTES.ROOT);
          }
        },
      })
    );
  };

  return (
    <Dialog
      open={open}
      onClose={resetForm}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, padding: 1 },
      }}
    >
      {/* Title with Icon */}
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AlertTriangle size={28} color="error" />
        <Typography variant="h6" fontWeight="bold">
          Delete Account
        </Typography>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: "1rem" }}>
          Are you sure you want to delete this account? Please tell us why.
        </DialogContentText>

        {/* Multiline Text Area */}
        <TextField
          autoFocus
          margin="dense"
          id="delete-reason"
          label="Reason for deletion"
          placeholder="Write your reason here..."
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={!!reason && reasonTooShort}
          helperText={reasonTooShort ? "Please provide at least 20 characters." : ""}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={resetForm}
          variant="text"
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          disabled={reasonTooShort}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 4,
            background: "linear-gradient(to right, #EF4444, #F97316)",
            "&:hover": {
              background: "linear-gradient(to right, #DC2626, #EA580C)",
            },
            "&:disabled": {
              background: "#e0e0e0",
            },
          }}
        >
          Delete Account
        </Button>
      </DialogActions>
    </Dialog>
  );
}
